import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { Col, Row, Wrapper } from '@ahmdigital/ui';
import { connect, useSelector } from 'react-redux';
import { isEmpty, isNil } from 'lodash/fp';
import { useEffect } from 'react';

import { getCookieForMembers } from '../../utils/cookie-for-members';
import { RootState } from '../../reducers';
import { useCookieContext } from '../../contexts/cookie';
import Awards from '../../components/awards';
import buildCorporationSchema from '../../components/structured-data/utils/build-corporation-schema';
import buildWebSiteSchema from '../../components/structured-data/utils/build-website-schema';
import ContentTilesHome from './components/content-tiles';
import CustomerActions, { CustomerActionType } from '../../actions/customer';
import FeaturesSection from '../../components/sections/features-section';
import getBannerProps from './utils/get-hero-section-content';
import getHomeHero from './utils/get-home-hero';
import getOfferOrPerkTile from './utils/get-offer-or-perk-tile';
import getWhyAhmSection from './utils/get-why-ahm-section';
import Head from '../../../utils/head';
import isSaleableProductId from '../../utils/is-saleable-product-id';
import PriceActions from '../../actions/price';
import PriceService, { Prices } from '../../models/price';
import ProductActions from '../../actions/product';
import ProductService from '../../models/product';
import selectCustomer from '../../selectors/select-customer';
import selectHasPartnerReferrer from '../../selectors/select-has-partner-referrer';
import selectLastViewedProduct from '../../selectors/select-last-viewed-product';
import selectReferralCode from '../../selectors/select-referral-code';
import StructuredData from '../../components/structured-data';
import styles from '../../components/sections/wrapped-section/styles.module.sass';
import useIsAfterHydration from '../../hooks/use-is-after-hydration';
import type { CmsBanner, ValuePropositionContent } from './types';
import type { ContentTilesProps } from './components/content-tiles';
import type { Customer } from '../../selectors/select-customer/types';
import type { Offer, Placeholder } from '../../components/offers/types';
import type { ProductType } from '../../types/product';

const { getPrice } = PriceService;
const { getProduct } = ProductService;

export type HomeProps = {
  actions: {
    customer: CustomerActionType['customer'];
    price: {
      load: (id: string, data: {}, options?: {}) => void;
    };
    product: {
      load: (id: string) => void;
    };
  } & CustomerActionType;
  content: {
    activeOffers: Offer[];
    banners: CmsBanner[];
    announcements: ContentTilesProps['items'];
    placeholders: Placeholder[];
    valuePropositionAhm: ValuePropositionContent;
  };
  customer: Customer;
  isPartnerReferral: boolean;
  lastViewedProductId: string | null;
  prices: Prices;
  products: Record<string, ProductType> | null;
};

const HomePage = ({
  actions,
  content,
  customer,
  isPartnerReferral,
  lastViewedProductId,
  prices,
  products,
}: HomeProps) => {
  const serverCookieValue = useCookieContext() as string;
  const memberNumber = getCookieForMembers('health', { serverCookieValue })?.id;
  const hasLastViewedProduct = !isNil(lastViewedProductId);
  const referralCode: string | null = useSelector(selectReferralCode);

  const isLastViewedProductSaleable = isSaleableProductId(lastViewedProductId);

  const validLastViewedProductId = isLastViewedProductSaleable ? lastViewedProductId : null;
  const lastViewedProduct = validLastViewedProductId ? getProduct(products, validLastViewedProductId) : null;

  const { load: loadProduct } = actions.product;
  const { load: loadPrice } = actions.price;

  const isAfterHydration = useIsAfterHydration();

  useEffect(() => {
    if (validLastViewedProductId && isEmpty(lastViewedProduct)) {
      loadProduct(validLastViewedProductId);
    }
  }, [lastViewedProduct, validLastViewedProductId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (validLastViewedProductId && !getPrice(prices, validLastViewedProductId, customer)) {
      loadPrice(validLastViewedProductId, customer);
    }
  }, [validLastViewedProductId, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasLastViewedProduct && !isLastViewedProductSaleable) {
      actions.customer.changeLastViewedProduct(null);
    }
  }, [actions.customer, hasLastViewedProduct, isLastViewedProductSaleable]);

  const whyAhmData = content?.valuePropositionAhm;
  const { whyAhmSectionTitle, whyAhmTileData } = getWhyAhmSection(whyAhmData);

  const { heroSectionProps, diversifiedHeroProps } = getBannerProps(content?.banners);
  const offerProps = {
    activeOffers: content?.activeOffers,
    placeholders: content?.placeholders,
  };

  return (
    <>
      <Head>
        <title>Cheap private health insurance and more</title>
        <meta
          name="description"
          content="Simple, smart and affordable health insurance for the things you’ll actually use. Browse Hospital & Extras cover. Plus travel, life, pet cover and more."
        />
      </Head>
      <StructuredData data={buildCorporationSchema()} />
      <StructuredData data={buildWebSiteSchema()} />
      <Wrapper size="extraExtraLarge">
        {getHomeHero({
          actions,
          diversifiedHeroProps,
          heroSectionProps,
          isAfterHydration,
          lastViewedProduct,
          memberNumber,
          offerProps,
        })}
        {getOfferOrPerkTile({
          hasReferralCode: !isNil(referralCode),
          isAfterHydration,
          isPartnerReferral,
          lastViewedProduct,
          offerProps,
          serverCookieValue,
        })}
        {whyAhmTileData && (
          <Row isBoxed>
            <Col df={12}>
              <div className={styles.horizontalSpacingWrapper}>
                <FeaturesSection
                  tiles={whyAhmTileData}
                  title={whyAhmSectionTitle || 'Why ahm?'}
                  withSimpleBit={false}
                />
              </div>
            </Col>
          </Row>
        )}
        <Awards />
        <ContentTilesHome items={content.announcements} />
      </Wrapper>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    customer: bindActionCreators(CustomerActions, dispatch),
    price: bindActionCreators(PriceActions, dispatch),
    product: bindActionCreators(ProductActions, dispatch),
  },
});

const mapStateToProps = (state: RootState) => ({
  customer: selectCustomer(state),
  isPartnerReferral: selectHasPartnerReferrer(state),
  lastViewedProductId: selectLastViewedProduct(state),
  prices: state.price.data,
  products: state.product.data,
});

export default connect(mapStateToProps, mapDispatchToProps)(HomePage);
export { HomePage as HomePageForTest, mapStateToProps };
